import { isEmpty, last, omit } from 'lodash-es'

export const useTech = defineStore('tech', () => {
  const certificationsStore = useCertifications()
  const techsMap = {
    vuejs: 'Vue.js',
    angular: 'Angular',
    javascript: 'JavaScript',
    nuxt: 'Nuxt.js',
    typescript: 'TypeScript',
    react: 'React'
  }
  const techs = Object.keys(techsMap)

  const techThemes = {
    vuejs: 'theme-vue',
    angular: 'theme-angular',
    javascript: 'theme-javascript',
    nuxt: 'theme-nuxt',
    typescript: 'theme-typescript',
    react: 'theme-react'
  }
  const officialTechs = ['vuejs', 'nuxt']
  const availableTechs = ['vuejs', 'nuxt', 'angular', 'javascript', 'react']
  const darkThemeEnabled = availableTechs

  const data = ref({})
  const copy = ref(null)
  const certifications = ref([])
  const selectedCertification = ref(null)
  const pages = ref([])
  const testimonials = ref([])

  const id = ref(null)

  const techLabel = computed(() => {
    return techsMap[id.value]
  })

  const isDarkThemeEnabled = computed(() => {
    return darkThemeEnabled.includes(id.value)
  })

  const docLink = computed(() => {
    return data.value?.doc_link
  })

  const indexPath = computed(() => {
    return data.value?.index_path
  })

  const isPresale = computed(() => {
    return data.value?.status === 'PREORDER'
  })
  const currentTheme = computed(() => techThemes[id.value])
  const lowestLevelTheme = useTechTheme().lowestLevelTheme

  function clear () {
    id.value = null
    data.value = {}
    copy.value = null
    certifications.value = []
    selectedCertification.value = null
    pages.value = []
  }

  async function load (tech = null) {
    console.log('Loading tech data...')
    const techId = tech || id.value

    if (isEmpty(data.value) && techId) {
      const response = await $fetch(`/api/techs/${techId}`)

      if (!response) {
        throw new Error('Tech not found')
      }

      data.value = omit(response, ['pages', 'certifications'])
      certifications.value = response?.certifications || []
      certificationsStore.selected = certifications.value?.find(cert => cert.difficulty_level === certifications.value.length)?.display_level
      pages.value = response?.pages || []

      // Load tech copy with graceful fallback:
      // 1) Try freeweekend-specific copy
      // 2) Fallback to empty object (no cross-tech fallback)
      const freeweekendCopies = import.meta.glob('~/assets/data/copy/freeweekend/*.json', { eager: true })

      const freeweekendKey = Object.keys(freeweekendCopies).find(k => k.endsWith(`/${id.value}.json`))

      const selectedModule = freeweekendKey
        ? freeweekendCopies[freeweekendKey]
        : null

      copy.value = selectedModule?.default || selectedModule || {}
    }

    return data.value
  }

  async function loadTestimonials () {
    const testimonialsResponse = await $fetch(`/api/testimonials/${id.value}`)
    testimonials.value = testimonialsResponse || []
  }

  function detectTechRaw (path, params) {
    let techId = ''
    if (params.tech) {
      techId = params.tech
    } else {
      const paths = path.split('/')
      const isSocialLoginCallback = paths.includes('login') && paths.includes('callback')
      let tech = isSocialLoginCallback ? last(paths) : paths[1]

      // Remove hash fragment from tech detection
      if (tech && tech.includes('#')) {
        tech = tech.split('#')[0]
      }

      techId = techs.includes(tech) ? tech : null
    }

    return techId
  }

  async function detectTech (path, params) {
    try {
      console.log('Detecting tech...')

      id.value = detectTechRaw(path, params)

      await load()

      console.log(`Tech: ${id.value}`)
    } catch (e) {
      console.error('Error detecting tech:', e)

      if (process.client) {
        throw createError({
          statusCode: 500,
          statusMessage: 'Something went wrong, Please reload the page.',
          data: 'reload'
        })
      }
    }
  }

  async function all () {
    try {
      console.log('Loading all techs...')
      const response = await $fetch('/api/techs')

      if (!response) {
        throw new Error('Techs not found')
      }

      return response
    } catch (e) {
      console.error('Error loading all techs:', e)
    }
  }

  const isOfficial = computed(() => officialTechs.includes(id.value || ''))

  return {
    load,
    loadTestimonials,
    detectTech,
    detectTechRaw,
    all,
    clear,
    lowestLevelTheme,

    id,
    data,
    copy,
    certifications,
    testimonials,
    isOfficial,
    selectedCertification,
    pages,
    isPresale,
    techs,
    techsMap,
    techThemes,
    currentTheme,
    techLabel,
    docLink,
    indexPath,
    isDarkThemeEnabled,
    darkThemeEnabled,
    availableTechs
  }
})

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useTech, import.meta.hot))
}
